"use client"

import * as React from "react"
import { cva, type VariantProps } from "class-variance-authority"
import { Tabs as TabsPrimitive } from "radix-ui"

import { cn } from "@/lib/utils"

function Tabs({
  className,
  orientation = "horizontal",
  ...props
}: React.ComponentProps<typeof TabsPrimitive.Root>) {
  return (
    <TabsPrimitive.Root
      data-slot="tabs"
      data-orientation={orientation}
      orientation={orientation}
      className={cn(
        "group/tabs flex gap-2 data-[orientation=horizontal]:flex-col",
        className
      )}
      {...props}
    />
  )
}

// V2 tabs (tabs.css) has two variants traced from the app: a transparent
// pill row (the default here) and a hairline-rail underline row (`line`).
// Neither carries a track background — the pill variant's "selected" state
// is the tab itself going tinted-accent, not a moving highlight behind it.
const tabsListVariants = cva(
  "group-data-[orientation=horizontal]/tabs:h-[var(--etn-control-height-sm)] data-[variant=line]:rounded-none data-[variant=line]:border-b data-[variant=line]:border-etn-border-hairline group/tabs-list text-muted-foreground inline-flex w-fit items-center justify-center gap-1 group-data-[orientation=vertical]/tabs:h-fit group-data-[orientation=vertical]/tabs:flex-col bg-transparent",
  {
    variants: {
      variant: {
        default: "",
        line: "gap-[26px]",
      },
    },
    defaultVariants: {
      variant: "default",
    },
  }
)

function TabsList({
  className,
  variant = "default",
  ...props
}: React.ComponentProps<typeof TabsPrimitive.List> &
  VariantProps<typeof tabsListVariants>) {
  return (
    <TabsPrimitive.List
      data-slot="tabs-list"
      data-variant={variant}
      className={cn(tabsListVariants({ variant }), className)}
      {...props}
    />
  )
}

function TabsTrigger({
  className,
  ...props
}: React.ComponentProps<typeof TabsPrimitive.Trigger>) {
  return (
    <TabsPrimitive.Trigger
      data-slot="tabs-trigger"
      className={cn(
        // Pill variant (default): 30px pill, tinted-accent when active — The
        // Tinted Accent Rule again, never a solid fill.
        "relative inline-flex h-[var(--etn-control-height-sm)] flex-1 items-center justify-center gap-1.5 rounded-etn-pill border border-transparent px-3 text-etn-control font-semibold whitespace-nowrap text-etn-text-label transition-all group-data-[orientation=vertical]/tabs:w-full group-data-[orientation=vertical]/tabs:justify-start focus-visible:ring-[3px] focus-visible:ring-ring/50 focus-visible:outline-1 disabled:pointer-events-none disabled:opacity-50 [&_svg]:pointer-events-none [&_svg]:shrink-0 [&_svg:not([class*='size-'])]:size-4",
        "hover:text-etn-text hover:bg-etn-surface-sunken data-[state=active]:bg-etn-accent-tint data-[state=active]:text-etn-accent-text-active data-[state=active]:hover:bg-etn-accent-tint",
        // Underline variant (line): transparent pill styling above still
        // applies its own hover bg, so switch it off and draw a 2px inset
        // rail under the active label instead.
        "group-data-[variant=line]/tabs-list:h-auto group-data-[variant=line]/tabs-list:rounded-none group-data-[variant=line]/tabs-list:px-0.5 group-data-[variant=line]/tabs-list:pb-[7px] group-data-[variant=line]/tabs-list:-mb-px group-data-[variant=line]/tabs-list:hover:bg-transparent group-data-[variant=line]/tabs-list:shadow-[inset_0_-2px_transparent]",
        "group-data-[variant=line]/tabs-list:data-[state=active]:bg-transparent group-data-[variant=line]/tabs-list:data-[state=active]:shadow-[inset_0_-2px_var(--etn-color-accent)]",
        className
      )}
      {...props}
    />
  )
}

function TabsContent({
  className,
  ...props
}: React.ComponentProps<typeof TabsPrimitive.Content>) {
  return (
    <TabsPrimitive.Content
      data-slot="tabs-content"
      className={cn("flex-1 outline-none", className)}
      {...props}
    />
  )
}

export { Tabs, TabsList, TabsTrigger, TabsContent, tabsListVariants }
